import React, { useState, useEffect } from 'react';
import { getScaleFactor } from '@/utils/qa';
import { Skeleton } from '@/components/shadcn/skeleton';

interface Props {
  onImageLoaded?: () => void;
  boundingBox?: number[];
  imageSrc?: string;
  limitWidth?: number;
  limitHeight?: number;
}

export default function ImageCropper({
  boundingBox,
  imageSrc,
  limitWidth = 338,
  limitHeight = 400,
  onImageLoaded,
}: Props) {
  const [imageWidth, setImageWidth] = useState<number>(0);
  const [imageHeight, setImageHeight] = useState<number>(0);
  const isComponentReady = imageWidth && imageHeight && boundingBox;

  useEffect(() => {
    let isMounted = true;
    if (imageSrc) {
      // Create an image element and load the image asynchronously
      const image = new Image();
      image.src = imageSrc;

      // Wait for the image to load and get its dimensions
      image.onload = () => {
        if (isMounted) {
          setImageWidth(image.width);
          setImageHeight(image.height);
          onImageLoaded?.();
        }
      };
    }
    return () => {
      isMounted = false;
    };
  }, [imageSrc, onImageLoaded]);

  // Calculate the coordinates of the cropping box
  const centerX = boundingBox?.[0] || 0;
  const centerY = boundingBox?.[1] || 0;
  const width = boundingBox?.[2] || 0;
  const height = boundingBox?.[3] || 0;

  // exact detection
  const initW = width * imageWidth;
  const initH = height * imageHeight;
  // with 20% margins
  const MARGIN = 0.2;
  const w = initW + initW * MARGIN;
  const h = initH + initH * MARGIN;
  const x = centerX * imageWidth - (width * imageWidth) / 2 - w * (MARGIN / 2);
  const y =
    centerY * imageHeight - (height * imageHeight) / 2 - h * (MARGIN / 2);

  // Make sure the new top-left corner is inside the image
  const croppedX = Math.max(x, 0);
  const croppedY = Math.max(y, 0);

  // Make sure the new bottom-right corner is inside the image
  const croppedWidth = Math.min(w, imageWidth - croppedX);
  const croppedHeight = Math.min(h, imageHeight - croppedY);

  const scaleFactor = getScaleFactor(w, h, limitWidth, limitHeight);
  const styles = {
    width: `${croppedWidth}px`,
    height: `${croppedHeight}px`,
    backgroundImage: `url(${imageSrc})`,
    backgroundPosition: `-${croppedX}px -${croppedY}px`,
    transformOrigin: 'top left',
    transform: `scale(${scaleFactor}) translate(-50%, -50%)`,
  };

  return (
    <div
      className="relative"
      style={{
        width: limitWidth,
        height: limitHeight,
      }}>
      {isComponentReady ? (
        <div className="absolute left-1/2 top-1/2" style={styles} />
      ) : (
        <Skeleton className="size-full border border-neutral-700 bg-neutral-800" />
      )}
    </div>
  );
}
